/**
 * Material 3 Theme Provider for Bonapisca
 * Comprehensive theming system integrating Material 3 Expressive with Mediterranean design
 */

import React from 'react';
import { ColorProvider } from '../contexts/ColorContext';
import { Material3ThemeProviderProps } from '../types/material3';

// Material 3 component theme configuration
const material3Theme = {
  button: {
    defaultProps: {
      ripple: true,
      className: "",
    },
    valid: {
      variants: ["filled", "outlined", "text", "elevated"],
      sizes: ["sm", "md", "lg"],
      colors: [
        "primary",
        "secondary", 
        "tertiary",
        "surface",
        "mediterranean-blue",
        "mediterranean-green",
        "mediterranean-gold",
        "mediterranean-coral",
      ],
    },
    styles: {
      base: {
        initial: {
          verticalAlign: "middle",
          userSelect: "select-none",
          fontFamily: "inherit",
          fontSize: "text-sm",
          fontWeight: "font-medium",
          textTransform: "none",
          letterSpacing: "tracking-normal",
          display: "inline-flex",
          alignItems: "items-center",
          justifyContent: "justify-center",
          gap: "gap-2",
          minWidth: "min-w-[64px]",
          minHeight: "min-h-[40px]",
          padding: "px-6 py-2.5",
          borderRadius: "rounded-full",
          transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "overflow-hidden",
          outline: "outline-none",
          cursor: "cursor-pointer",
          border: "border-0",
          lineHeight: "leading-tight",
          textDecoration: "no-underline",
        },
        hover: {
          boxShadow: "shadow-md",
          transform: "scale-105",
        },
        focus: {
          outline: "outline-2 outline-primary outline-offset-2",
        },
        active: {
          transform: "scale-95",
        },
        disabled: {
          opacity: "opacity-50",
          cursor: "cursor-not-allowed",
          pointerEvents: "pointer-events-none",
        },
      },
      variants: {
        filled: {
          "primary": {
            background: "bg-primary-500",
            color: "text-white",
            boxShadow: "shadow-elevation-1",
          },
          "secondary": {
            background: "bg-secondary-500", 
            color: "text-white",
            boxShadow: "shadow-elevation-1",
          },
          "tertiary": {
            background: "bg-tertiary-500",
            color: "text-white", 
            boxShadow: "shadow-elevation-1",
          },
        },
        outlined: {
          "primary": {
            background: "bg-transparent",
            color: "text-primary-500",
            border: "border border-primary-500",
          },
          "secondary": {
            background: "bg-transparent",
            color: "text-secondary-500",
            border: "border border-secondary-500",
          },
          "tertiary": {
            background: "bg-transparent",
            color: "text-tertiary-500", 
            border: "border border-tertiary-500",
          },
        },
        elevated: {
          "primary": {
            background: "bg-surface-100",
            color: "text-primary-500",
            boxShadow: "shadow-elevation-1",
          },
          "secondary": {
            background: "bg-surface-100",
            color: "text-secondary-500",
            boxShadow: "shadow-elevation-1",
          },
          "tertiary": {
            background: "bg-surface-100", 
            color: "text-tertiary-500",
            boxShadow: "shadow-elevation-1",
          },
        },
        text: {
          "primary": {
            background: "bg-transparent",
            color: "text-primary-500",
          },
          "secondary": {
            background: "bg-transparent",
            color: "text-secondary-500", 
          },
          "tertiary": {
            background: "bg-transparent",
            color: "text-tertiary-500",
          },
        },
      },
      sizes: {
        sm: {
          fontSize: "text-xs",
          padding: "px-4 py-2",
          minHeight: "min-h-[32px]",
        },
        md: {
          fontSize: "text-sm", 
          padding: "px-6 py-2.5",
          minHeight: "min-h-[40px]",
        },
        lg: {
          fontSize: "text-base",
          padding: "px-8 py-3",
          minHeight: "min-h-[48px]",
        },
      },
    },
  },
  card: {
    defaultProps: {
      variant: "elevated",
      color: "surface",
      shadow: true,
      className: "",
    },
    valid: {
      variants: ["elevated", "filled", "outlined"],
      colors: ["surface", "primary", "secondary", "tertiary"],
    },
    styles: {
      base: {
        initial: {
          position: "relative",
          display: "flex",
          flexDirection: "flex-col",
          wordWrap: "break-word",
          backgroundColor: "bg-surface",
          borderRadius: "rounded-xl",
          transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        },
        hover: {
          transform: "translateY(-1px)",
          boxShadow: "shadow-elevation-2",
        },
      },
      variants: {
        elevated: {
          surface: {
            backgroundColor: "bg-surface",
            boxShadow: "shadow-elevation-1",
          },
        },
        filled: {
          surface: {
            backgroundColor: "bg-surface-200",
          },
        },
        outlined: {
          surface: {
            backgroundColor: "bg-surface",
            border: "border border-outline",
          },
        },
      },
    },
  },
  input: {
    defaultProps: {
      variant: "outlined",
      size: "md",
      color: "primary",
      labelProps: {},
      className: "",
    },
    valid: {
      variants: ["standard", "outlined", "static"],
      sizes: ["md", "lg"],
      colors: ["primary", "secondary", "tertiary"],
    },
    styles: {
      base: {
        container: {
          position: "relative",
          width: "w-full",
          minWidth: "min-w-[200px]",
        },
        input: {
          peer: "peer",
          width: "w-full",
          height: "h-full",
          backgroundColor: "bg-transparent",
          fontFamily: "font-sans",
          fontWeight: "font-normal",
          outline: "outline outline-0 focus:outline-0",
          disabled: "disabled:bg-blue-gray-50 disabled:border-0",
          transition: "transition-all",
        },
        label: {
          display: "flex",
          width: "w-full",
          height: "h-full",
          userSelect: "select-none",
          pointerEvents: "pointer-events-none",
          position: "absolute",
          left: "left-0",
          fontWeight: "font-normal",
          transition: "transition-all",
        },
      },
      variants: {
        outlined: {
          base: {
            input: {
              borderRadius: "rounded-md",
              border: "border border-outline",
              padding: "px-3 py-3",
              fontSize: "text-sm",
              color: "text-on-surface",
              background: "bg-surface",
              "&:focus": {
                borderColor: "border-primary-500",
                borderWidth: "border-2",
              },
              "&:disabled": {
                opacity: "opacity-50",
                cursor: "cursor-not-allowed",
              },
            },
            label: {
              position: "absolute",
              left: "left-3",
              top: "top-3",
              fontSize: "text-sm",
              color: "text-on-surface-variant",
              background: "bg-surface",
              padding: "px-1",
              transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
              "&.focused": {
                transform: "translateY(-24px) scale(0.875)",
                color: "text-primary-500",
              },
            },
          },
        },
      },
      sizes: {
        md: {
          container: {
            height: "h-11",
          },
          input: {
            fontSize: "text-sm",
            padding: "px-3 py-3",
          },
          label: {
            fontSize: "text-sm",
          },
        },
        lg: {
          container: {
            height: "h-12",
          },
          input: {
            fontSize: "text-base",
            padding: "px-4 py-4",
          },
          label: {
            fontSize: "text-base",
          },
        },
      },
    },
  },
  select: {
    defaultProps: {
      variant: "outlined",
      size: "md",
      color: "primary",
      className: "",
    },
    valid: {
      variants: ["standard", "outlined", "static"],
      sizes: ["md", "lg"],
      colors: ["primary", "secondary", "tertiary"],
    },
    styles: {
      base: {
        container: {
          position: "relative",
          width: "w-full",
          minWidth: "min-w-[200px]",
        },
        select: {
          peer: "peer",
          width: "w-full",
          height: "h-full",
          backgroundColor: "bg-surface",
          fontFamily: "font-sans",
          fontWeight: "font-normal",
          outline: "outline-0",
          transition: "transition-all",
          cursor: "cursor-pointer",
        },
      },
      variants: {
        outlined: {
          base: {
            select: {
              borderRadius: "rounded-md",
              border: "border border-outline",
              padding: "px-3 py-3",
              fontSize: "text-sm",
              color: "text-on-surface",
              "&:focus": {
                borderColor: "border-primary-500",
                borderWidth: "border-2",
              },
            },
          },
        },
      },
    },
  },
  typography: {
    defaultProps: {
      variant: "paragraph",
      color: "inherit",
      textGradient: false,
      className: "",
    },
    valid: {
      variants: [
        "display-large",
        "display-medium", 
        "display-small",
        "headline-large",
        "headline-medium",
        "headline-small",
        "title-large",
        "title-medium",
        "title-small",
        "body-large",
        "body-medium",
        "body-small",
        "label-large",
        "label-medium",
        "label-small",
      ],
      colors: [
        "inherit",
        "current",
        "black",
        "white",
        "primary",
        "secondary",
        "tertiary",
        "on-surface",
        "on-surface-variant",
      ],
    },
    styles: {
      variants: {
        "display-large": {
          fontSize: "text-6xl",
          lineHeight: "leading-none",
          fontWeight: "font-normal",
          letterSpacing: "tracking-tight",
        },
        "display-medium": {
          fontSize: "text-5xl",
          lineHeight: "leading-tight",
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "display-small": {
          fontSize: "text-4xl",
          lineHeight: "leading-tight", 
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "headline-large": {
          fontSize: "text-3xl",
          lineHeight: "leading-tight",
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "headline-medium": {
          fontSize: "text-2xl",
          lineHeight: "leading-tight",
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "headline-small": {
          fontSize: "text-xl",
          lineHeight: "leading-snug",
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "title-large": {
          fontSize: "text-lg",
          lineHeight: "leading-snug",
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "title-medium": {
          fontSize: "text-base",
          lineHeight: "leading-normal",
          fontWeight: "font-medium",
          letterSpacing: "tracking-wide",
        },
        "title-small": {
          fontSize: "text-sm",
          lineHeight: "leading-normal",
          fontWeight: "font-medium",
          letterSpacing: "tracking-wide",
        },
        "body-large": {
          fontSize: "text-base",
          lineHeight: "leading-relaxed",
          fontWeight: "font-normal",
          letterSpacing: "tracking-wide",
        },
        "body-medium": {
          fontSize: "text-sm",
          lineHeight: "leading-normal",
          fontWeight: "font-normal",
          letterSpacing: "tracking-normal",
        },
        "body-small": {
          fontSize: "text-xs",
          lineHeight: "leading-normal",
          fontWeight: "font-normal",
          letterSpacing: "tracking-wide",
        },
        "label-large": {
          fontSize: "text-sm",
          lineHeight: "leading-normal",
          fontWeight: "font-medium",
          letterSpacing: "tracking-wide",
        },
        "label-medium": {
          fontSize: "text-xs",
          lineHeight: "leading-normal",
          fontWeight: "font-medium",
          letterSpacing: "tracking-wider",
        },
        "label-small": {
          fontSize: "text-xs",
          lineHeight: "leading-normal",
          fontWeight: "font-medium",
          letterSpacing: "tracking-wider",
        },
      },
    },
  },
} as const;

/**
 * Comprehensive Material 3 Theme Provider
 * Integrates all Material 3 systems with Mediterranean context
 */
export const Material3ThemeProvider: React.FC<Material3ThemeProviderProps> = ({
  children,
  defaultColorScheme = 'auto',
  mediterraneanPreset = true,
}) => {
  return (
    <ColorProvider
      defaultPreset={mediterraneanPreset ? 'classic' : undefined}
      enableSystemTheme={defaultColorScheme === 'auto'}
    >
      {children}
    </ColorProvider>
  );
};

// Export the theme configuration for external use
export { material3Theme };

// Utility hook for accessing theme values
export const useMaterial3Theme = () => {
  return material3Theme;
};